import { useEffect, useState } from 'react'
import { useInvoiceReading } from '@/hooks/use-billing'
import { fetchInvoiceReadingPhoto } from '@/lib/billing'
import { formatKES } from '@/lib/utils'

function formatUnits(value: number, unitLabel: string | null): string {
  const rounded = Math.round(value * 100) / 100
  return unitLabel ? `${rounded} ${unitLabel}` : String(rounded)
}

function ReadingPhoto({ invoiceId }: { invoiceId: string }) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false
    fetchInvoiceReadingPhoto(invoiceId)
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setUrl(objectUrl)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [invoiceId])

  if (error) return null
  if (!url) {
    return <div className="h-32 w-32 animate-pulse rounded border border-border-2 bg-cream-2" />
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" className="inline-block">
      <img
        src={url}
        alt="Meter reading photo"
        className="h-32 w-32 rounded border border-border-2 object-cover transition-opacity hover:opacity-90"
      />
    </a>
  )
}

/** Previous/current reading, units, and rate behind a meter-based invoice — lets a member verify
 * their own bill instead of taking the amount on faith. Renders nothing for a flat-fee invoice
 * (no reading exists) or while that's still being determined, so it's safe to drop into any
 * invoice detail view unconditionally. */
export function InvoiceReadingPanel({ invoiceId }: { invoiceId: string }) {
  const { data, isLoading } = useInvoiceReading(invoiceId)
  const reading = data?.data

  if (isLoading || !reading) return null

  return (
    <div className="mt-4 space-y-3 rounded border border-border-2 bg-cream-2 px-4 py-3">
      <p className="font-sans text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
        Meter reading
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 font-sans text-sm sm:grid-cols-4">
        <div>
          <p className="text-xs text-muted-foreground">Previous</p>
          <p className="text-ink">{formatUnits(reading.previousReading, reading.unitLabel)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Current</p>
          <p className="text-ink">{formatUnits(reading.currentReading, reading.unitLabel)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Consumed</p>
          <p className="text-ink">{formatUnits(reading.unitsConsumed, reading.unitLabel)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Rate</p>
          <p className="text-ink">
            {formatKES(reading.ratePerUnit)}
            {reading.unitLabel ? ` / ${reading.unitLabel}` : ''}
          </p>
        </div>
      </div>
      {reading.standardCharge > 0 && (
        <div className="space-y-1 border-t border-border-2 pt-2 font-sans text-sm">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Consumption ({formatUnits(reading.unitsConsumed, reading.unitLabel)} × rate)
            </span>
            <span>{formatKES(reading.unitsConsumed * reading.ratePerUnit)}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Standard charge</span>
            <span>{formatKES(reading.standardCharge)}</span>
          </div>
          <div className="flex items-center justify-between font-medium text-ink">
            <span>Total</span>
            <span>
              {formatKES(reading.unitsConsumed * reading.ratePerUnit + reading.standardCharge)}
            </span>
          </div>
        </div>
      )}
      {reading.hasPhoto && <ReadingPhoto invoiceId={invoiceId} />}
    </div>
  )
}
